/**
 * Fresh-login marker for login default-profile resolution (ADR-033 §3.2,
 * amended 2026-06-08).
 *
 * # Why a marker
 *
 * The login default-profile resolution (``preferences-provider``) must run on a
 * **fresh sign-in** but **not** on a reload within the same session — otherwise
 * a reload would re-apply the user's chosen login-default Profile over the
 * persisted working state, discarding the unsaved overrides they accumulated
 * this session. There is no "is this a fresh login?" signal in the bootstrap
 * payload (the BFF only round-trips the ``default_profile`` pointer — the UI
 * owns the resolution), so the UI records a **per-user marker** the first time
 * it resolves, and skips re-resolving while that marker is present.
 *
 * # Why ``localStorage`` (not ``sessionStorage``), keyed by user id
 *
 *   - **``localStorage``** so a *second browser tab* opened in the same session
 *     finds the marker already set and does **not** re-apply over the working
 *     state. ``sessionStorage`` is per-tab and would re-fire in every new tab.
 *   - **Keyed by user id** (``meridian.default_applied.<user_id>``) so
 *     **user-switching on a shared browser** is handled: signing in as a
 *     different user finds no marker for *that* id and resolves their default
 *     correctly. Mirrors the per-user keying convention of
 *     :mod:`preferences/cache` (``meridian.<network>.preferences.<user_id>``).
 *
 * The marker is **cleared on logout** (:func:`clearAllDefaultAppliedMarkers`,
 * wired into the api-client ``logout()`` path) so the next sign-in is treated
 * as fresh and re-applies the login-default. Logout has no user id in hand, so
 * the clear is a prefix scan over every ``meridian.default_applied.*`` key —
 * correct because logout ends the only session.
 *
 * # Degrade-safe (localStorage disabled / blocked)
 *
 * Every function is **best-effort**: when ``localStorage`` is unavailable
 * (private browsing, quota exhausted, SSR) reads return ``false`` (⇒ "fresh
 * login" ⇒ apply once per load) and writes/clears no-op. This degrades to
 * "apply the default on every load" rather than "never apply" — the
 * conservative, fail-safe direction per CLAUDE.md §8.1 (the user always gets a
 * resolved working state; they only lose the cross-reload persistence of
 * unsaved overrides, which a storage-disabled browser can't carry anyway).
 *
 * Cross-references:
 *   - ADR-033 §3.2 (login default-profile resolution — persist-across-reload,
 *     default-on-fresh-login).
 *   - REQ-UI-093 (default applied at login).
 *   - preferences/cache.ts (the sibling per-user localStorage convention).
 */

/** The marker-key prefix — one entry per user id. The logout clear scans for
 *  this prefix; the per-user functions append the id. */
const MARKER_PREFIX = "meridian.default_applied.";

/**
 * Build the per-user marker key: ``meridian.default_applied.<user_id>``.
 * Exported so tests can assert the exact key the provider writes.
 */
export function makeDefaultAppliedKey(userId: string): string {
  return `${MARKER_PREFIX}${userId}`;
}

/**
 * Has the login default already been applied for this user (⇒ this is a
 * reload / subsequent load within the same sign-in, not a fresh login)?
 * Returns ``false`` when the marker is absent **or** ``localStorage`` is
 * unavailable — the degrade-safe "treat as fresh login" direction.
 */
export function hasDefaultApplied(userId: string): boolean {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return false;
  }
  try {
    return window.localStorage.getItem(makeDefaultAppliedKey(userId)) !== null;
  } catch {
    // Storage read threw (disabled / blocked) — treat as a fresh login so the
    // user still gets their default applied this load.
    return false;
  }
}

/**
 * Record that the login default has been applied for this user, so subsequent
 * loads within the same sign-in skip re-applying. Best-effort: no-ops when
 * ``localStorage`` is unavailable (the resolution then re-runs each load —
 * degrade-safe).
 */
export function markDefaultApplied(userId: string): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return;
  }
  try {
    // The value is a wall-clock stamp for debuggability only; presence is what
    // matters (mirrors the cache's ``cached_at`` posture).
    window.localStorage.setItem(makeDefaultAppliedKey(userId), new Date().toISOString());
  } catch {
    // Quota / disabled — the marker simply won't persist; resolution re-runs
    // next load. Acceptable (same posture as writePreferencesCache).
  }
}

/**
 * Clear **every** per-user fresh-login marker — called from the api-client
 * ``logout()`` path so the next sign-in (any user) is treated as fresh and
 * re-applies the login-default Profile. Logout carries no user id, so this
 * scans all ``meridian.default_applied.*`` keys; that is correct because logout
 * ends the only session. Best-effort; no-ops when ``localStorage`` is
 * unavailable.
 */
export function clearAllDefaultAppliedMarkers(): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return;
  }
  try {
    // Collect first, then remove — mutating localStorage while iterating its
    // index by ``key(i)`` shifts the indices and can skip entries.
    const toRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key !== null && key.startsWith(MARKER_PREFIX)) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Same best-effort posture — a failed clear just means the next sign-in on
    // the same browser skips re-applying; the user can re-apply manually.
  }
}
